import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';

import { loadDocumentTypes } from '../../../store/directusService';
import {
    authoriseFormParticipant,
    loadForm,
    reopenFormParticipant,
    updateFormParticipant,
    withdrawFormParticipant
} from '../../../store/participantService';
import { loadUser } from '../../../store/userService';
import { hasRole, MANAGER, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import AddDocument from '../../documents/AddDocument';
import Button from '../../formElements/Button';

import UCD603FormTemplate from './formUCD603/UCD603FormTemplate';
import UCD604FormTemplate from './formUCD604/UCD604FormTemplate';
import { themeJson } from './FormTheme';
import GenerateForm from './GenerateForm';
import GenerateStandardForm from './GenerateStandardForm';

import form from '../../../commonStyles/formStyles.module.css';
import actions from '../../ui/formActions/formActions.module.css';

const acceptedRoles = [MANAGER, QUALITY, SUPERUSER];

const EditForm = ({ row, adviser, onClose, roles }) => {
    //HOOKS
    const dispatch = useDispatch();

    //LOCAL STATE
    const [surveyModel, setSurveyModel] = useState(new Model('{}'));
    const [isSucceeded, setIsSucceeded] = useState(false);
    const [showDocumentDownload, setShowDocumentDownload] = useState(false);
    const [docType, setDocType] = useState(false);
    const [generatePdf, setGeneratePdf] = useState(false);
    const [generateUCD603Pdf, setGenerateUCD603Pdf] = useState(false);
    const [generateUCD604Pdf, setGenerateUCD604Pdf] = useState(false);
    const [fileName, setFileName] = useState('');
    const [surveyComplete, setSurveyComplete] = useState(false);
    const [authorisedButtonDisabled, setAuthorisedButtonDisabled] = useState(false);
    const [withdrawnButtonDisabled, setWithdrawnButtonDisabled] = useState(false);
    const [reopenButtonDisabled, setReopenButtonDisabled] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    //STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentForm = useSelector((state) => state.entities.participantService.currentForm);
    const currentFormParticipant = useSelector(
        (state) => state.entities.participantService.currentFormParticipant
    );
    const formStatusDetails = useSelector(
        (state) => state.entities.directusService.formStatusDetails
    );
    const documentTypes = useSelector((state) => state.entities.directusService.documentTypes);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles));
        dispatch(loadForm(row.formId));
        documentTypes.length < 1 && dispatch(loadDocumentTypes());
    }, []);

    useEffect(() => {
        if (documentTypes.length < 1) return;
        setDocType(documentTypes.find((el) => el.name === 'Form'));
    }, [documentTypes]);

    useEffect(() => {
        surveyModel.data = row.data;
    }, [surveyModel, row]);

    useEffect(() => {
        if (currentParticipant.userId) {
            dispatch(loadUser(currentParticipant.userId));
        }
    }, [currentParticipant]);

    useEffect(() => {
        if (Object.keys(currentForm).length < 1) return;

        const model = new Model(currentForm.data);
        model.applyTheme(themeJson);
        model.showQuestionNumbers = true;
        setSurveyModel(model);
    }, [currentForm]);

    useEffect(() => {
        if (surveyComplete) {
            const payload = {
                id: row.id,
                participantId: currentParticipant.id,
                statusId: formStatusDetails.find((el) => el.name === 'Open')?.id || '',
                pdfName: currentForm.name,
                formId: currentForm.id,
                data: surveyModel.data
            };
            dispatch(updateFormParticipant(payload));
        }
    }, [surveyComplete]);

    useEffect(() => {
        if (successMessage === `Form participant has been authorised successfully`) {
            if (currentForm.name.includes('UCD604')) setGenerateUCD604Pdf(true);
            else if (currentForm.name.includes('603')) setGenerateUCD603Pdf(true);
            else setGeneratePdf(true);
            setAuthorisedButtonDisabled(true);
            setWithdrawnButtonDisabled(true);
            setReopenButtonDisabled(true);
        } else if (
            successMessage === `Form participant has been updated successfully` ||
            successMessage === `Form participant has been reopened successfully` ||
            successMessage === `Form participant has been withdrawn successfully`
        ) {
            onFormExit();
        } else if (
            successMessage.startsWith(`Document`) &&
            successMessage.endsWith(`has been uploaded`)
        )
            onFormExit();
    }, [successMessage]);

    // EVENT HANDLERS
    const onFormExit = () => onClose();

    const onExit = () => {
        setGeneratePdf(false);
        setIsSucceeded(false);
        setShowDocumentDownload(false);
    };

    const onSurveyComplete = () => setSurveyComplete(true);

    const onAuthorise = () => {
        setFileName(`${currentForm.name}_${format(new Date(), `dd_MMM_yyyy_HH_mm`)}`);
        dispatch(authoriseFormParticipant(row.id));
    };

    const onWithdraw = () => dispatch(withdrawFormParticipant(row.id));

    const onReopen = () => dispatch(reopenFormParticipant(row.id));

    //  EVENT HANDLER CALLBACKS
    const onPdfGenerated = (isPdfGenerated) => {
        setIsSucceeded(isPdfGenerated);
        setShowDocumentDownload(isPdfGenerated);
    };

    // RENDER
    return (
        <div>
            <Survey model={surveyModel} onComplete={onSurveyComplete} />
            <div className={actions.formActions}>
                <Button
                    id="authoriseButton"
                    disabled={
                        isDisabled ||
                        row.status.toLowerCase() === 'authorised' ||
                        authorisedButtonDisabled
                    }
                    type="button"
                    content="Authorise"
                    onClick={onAuthorise}
                />
                <Button
                    id="withdrawButton"
                    disabled={
                        isDisabled ||
                        row.status.toLowerCase() === 'withdrawn' ||
                        withdrawnButtonDisabled
                    }
                    type="button"
                    content="Withdraw"
                    onClick={onWithdraw}
                />
                <Button
                    id="reopenButton"
                    disabled={
                        isDisabled || row.status.toLowerCase() === 'open' || reopenButtonDisabled
                    }
                    type="button"
                    content="Re-Open"
                    onClick={onReopen}
                />
                <div className={actions.cancelLink} onClick={onFormExit}>
                    Cancel X
                </div>
            </div>
            {generatePdf && (
                <GenerateStandardForm
                    surveyModel={surveyModel}
                    form={currentForm}
                    row={row}
                    currentParticipant={currentParticipant}
                    participantsAdviser={adviser}
                    onPdfGenerated={onPdfGenerated}
                    onClose={() => setGeneratePdf(false)}
                />
            )}
            {generateUCD604Pdf && (
                <GenerateForm
                    fileName={fileName}
                    formCode={currentForm.code}
                    ptCode={currentParticipant.ptCode}
                    userCode={loggedInUser?.code || ''}
                    onPdfGenerated={onPdfGenerated}
                    onClose={() => setGenerateUCD604Pdf(false)}>
                    <UCD604FormTemplate surveyAnswers={currentFormParticipant.data} />
                </GenerateForm>
            )}
            {generateUCD603Pdf && (
                <GenerateForm
                    fileName={fileName}
                    formCode={currentForm.code}
                    ptCode={currentParticipant.ptCode}
                    userCode={loggedInUser?.code || ''}
                    onPdfGenerated={onPdfGenerated}
                    onClose={() => setGenerateUCD603Pdf(false)}>
                    <UCD603FormTemplate surveyAnswers={currentFormParticipant.data} />
                </GenerateForm>
            )}
            {isSucceeded && (
                <div className={form.successMessage}>PDF {currentForm.name} downloaded</div>
            )}
            {showDocumentDownload && (
                <div>
                    <AddDocument
                        onClose={onExit}
                        onCancel={onExit}
                        isDisabled={isDisabled}
                        btnText="Close"
                        currentDocumentType={docType ? docType : null}
                    />
                </div>
            )}
        </div>
    );
};

EditForm.propTypes = {
    row: PropTypes.object.isRequired,
    adviser: PropTypes.object,
    onClose: PropTypes.func,
    roles: PropTypes.arrayOf(PropTypes.string)
};

export default EditForm;
